import axios from "axios";
import { unauthorizedInterceptors } from "../utilities/functions";

// Configure the axios instance
const reportsAPI = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/Report`,
  headers: {
    "Content-Type": "application/json",
  },
});

unauthorizedInterceptors(reportsAPI);

// Set Authorization Header
const setAuthHeader = () => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    reportsAPI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};

// === Report API Functions ===

// Get all reports
export const getAllReports = async (filters = {}) => {
  setAuthHeader();
  return await reportsAPI.get("api/report-files/", {
    params: {
      client_ids: filters.client_ids || [],
      brand_ids: filters.brand_ids || [],
    },
  });
};

// Get a specific report by ID
export const getReportById = async (id) => {
  setAuthHeader();
  return await reportsAPI.get(`api/report-files/${id}/`);
};

// Get list segments
export const getListSegments = async (filters = {}) => {
  setAuthHeader();
  console.log("Filters:1", filters);
  
  // Create URLSearchParams to properly format array parameters for Django
  const params = new URLSearchParams();
  
  // Add scalar parameters
  if (filters.list_name) params.append("list_name", filters.list_name);
  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);
  if (filters.performance_field) params.append("performance_field", filters.performance_field);
  if (filters.product) params.append("product", filters.product);
  if (filters.category) params.append("category", filters.category);
  
  // Add array parameters with repeated keys (Django's expected format)
  if (filters.client_ids && filters.client_ids.length > 0) {
    filters.client_ids.forEach(id => params.append("client_ids", id));
  }
  
  if (filters.brand_ids && filters.brand_ids.length > 0) {
    filters.brand_ids.forEach(id => params.append("brand_ids", id));
  }
  
  // Construct the endpoint URL with parameters
  const endpoint = `api/report-files/get-list-segments/${
    params.toString() ? `?${params.toString()}` : ""
  }`;
  
  return await reportsAPI.get(endpoint);
};

// Get products for dropdown
export const getProducts = async () => {
  setAuthHeader();
  return await reportsAPI.get("api/report-files/get-products/");
};

// Get categories for dropdown
export const getCategories = async () => {
  setAuthHeader();
  return await reportsAPI.get("api/report-files/get-categories/");
};

// Get the processed data of the latest report or by ID
export const getProcessedReport = async (
  id = null,
  filters = {},
  request_type = "process",
  file_to_compare_id = null
) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (id) {
    params.append("id", id);
  }

  if (request_type) {
    params.append("request_type", request_type);
  }

  if (file_to_compare_id) {
    params.append("file_to_compare_id", file_to_compare_id);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      value.forEach((v) => params.append(key.toLowerCase(), v));
    } else if (
      value !== null &&
      value !== undefined &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      params.append(key.toLowerCase(), value);
    }
  });

  const endpoint = `api/report-files/processed-reports/${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  return await reportsAPI.get(endpoint);
};

export const processListUsage = async (
  client_ids = [],
  brand_ids = [],
  startDate,
  endDate,
  performanceField
) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (client_ids?.length > 0) {
    client_ids.forEach((id) => params.append("client_ids", id));
  }

  if (brand_ids?.length > 0) {
    brand_ids.forEach((id) => params.append("brand_ids", id));
  }

  if (startDate) {
    params.append("start_date", startDate.format("YYYY-MM-DD"));
  }

  if (endDate) {
    params.append("end_date", endDate.format("YYYY-MM-DD"));
  }

  if (performanceField) {
    params.append("performance_field", performanceField);
  }

  const endpoint = `api/report-files/process-list-usage/${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  return await reportsAPI.get(endpoint);
};

// Upload a new report
export const uploadReport = async (fileData) => {
  setAuthHeader();
  return await reportsAPI.post("api/report-files/", fileData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};
// Upload a new report
export const updateReport = async (id, fileData) => {
  setAuthHeader();
  return await reportsAPI.patch(`api/report-files/${id}/`, fileData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Delete a report by ID
export const deleteReport = async (id) => {
  setAuthHeader();
  return await reportsAPI.delete(`api/report-files/${id}/`);
};

// === Report Type API Functions ===

// Get all report types
export const getAllReportTypes = async () => {
  setAuthHeader();
  return await reportsAPI.get("api/report-types/");
};

// Create a new report type
export const createReportType = async (data) => {
  setAuthHeader();
  return await reportsAPI.post("api/report-types/", data);
};

// Fetch all parent sources
export const fetchParentSources = async () => {
  setAuthHeader();
  return await reportsAPI.get("api/parent-sources/");
};

// Create a new parent source
export const createParentSource = async (data) => {
  setAuthHeader();
  return await reportsAPI.post("api/parent-sources/", data);
};
export const updateParentSource = async (id, data) => {
  setAuthHeader();
  return await reportsAPI.patch(`api/parent-sources/${id}/`, data);
};

export const createChildSource = async (data) => {
  setAuthHeader();
  return await reportsAPI.post("api/child-sources/", data);
};

export const fetchChildSources = async () => {
  setAuthHeader();
  return await reportsAPI.get("api/child-sources/");
};

export const fetchAllSources = async () => {
  setAuthHeader();
  return await reportsAPI.get("api/sources/all/");
};

// Create a new Sales Update Report
export const createSalesUpdateReport = async (data) => {
  setAuthHeader();
  return await reportsAPI.post("api/sales-update-reports/", data);
};

// Fetch all Sales Update Reports
export const fetchSalesUpdateReports = async () => {
  setAuthHeader();
  return await reportsAPI.get("api/sales-update-reports/");
};

// Upload a file to a Sales Update Report
export const uploadSalesUpdateReportFile = async (data) => {
  setAuthHeader();
  return await reportsAPI.post("api/sales-update-report-files/", data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Update an existing Sales Update Report
export const updateSalesUpdateReport = async (id, data) => {
  setAuthHeader();
  return await reportsAPI.patch(`api/sales-update-reports/${id}/`, data);
};

// Delete a Sales Update Report
export const deleteSalesUpdateReport = async (id) => {
  setAuthHeader();
  return await reportsAPI.delete(`api/sales-update-reports/${id}/`);
};

// Fetch all Sales Update Report Files
export const fetchSalesUpdateReportFiles = async () => {
  setAuthHeader();
  return await reportsAPI.get("api/sales-update-report-files/");
};

// Generate a Sales Update Report based on start and end date
export const generateSalesUpdateReport = async (startDate, endDate) => {
  setAuthHeader();
  return await reportsAPI.get("api/sales-update-reports/generate-report/", {
    params: {
      start_date: startDate,
      end_date: endDate,
    },
  });
};

export const getCommentByTab = async (tabName) => {
  setAuthHeader();
  return await reportsAPI.get(`api/comments/?tab=${tabName}`);
};

export const updateComment = async (commentId, data) => {
  setAuthHeader();
  return await reportsAPI.put(`api/comments/${commentId}/`, data);
};

export const createSalesDumpFile = async (data) => {
  setAuthHeader();
  return await reportsAPI.post("api/sales-dump-file/", data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getLastSalesDumpUploadDate = async (brandId) => {
  setAuthHeader();
  return await reportsAPI.get(`api/sales-dump-file/last-upload/?brand_id=${brandId}`);
};

export const updateSalesDumpFile = async (id, data) => {
  setAuthHeader();
  return await reportsAPI.put(`api/sales-dump-file/${id}/`, data);
};

export const getSalesDumpFileById = async (id) => {
  setAuthHeader();
  return await reportsAPI.get(`api/sales-dump-file/${id}/`);
};

export const getLastUploadDate = async (brandId) => {
  setAuthHeader();
  return await reportsAPI.get(`api/sales-dump-file/last-upload/`, {
    params: { brand_id: brandId },
  });
};

export const processSales = async (
  client_ids = [],
  brand_ids = [],
  lists = [],
  segments = [],
  mail_codes = [],
  products = [],
  categories = []
) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (client_ids?.length > 0) {
    client_ids.forEach((id) => params.append("client_ids[]", id));
  }

  if (brand_ids?.length > 0) {
    brand_ids.forEach((id) => params.append("brand_ids[]", id));
  }

  if (lists?.length > 0) {
    lists.forEach((list) => params.append("lists[]", list));
  }

  if (segments?.length > 0) {
    segments.forEach((segment) => params.append("segments[]", segment));
  }

  if (mail_codes?.length > 0) {
    mail_codes.forEach((code) => params.append("mail_codes[]", code));
  }

  if (products?.length > 0) {
    products.forEach((product) => params.append("products[]", product));
  }

  if (categories?.length > 0) {
    categories.forEach((category) => params.append("categories[]", category));
  }

  const endpoint = `api/product-order-data/process-sales/${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  return await reportsAPI.get(endpoint);
};

export const get100DaysROR = async (client_ids = []) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (client_ids?.length > 0) {
    client_ids.forEach((id) => params.append("client_ids[]", id));
  }

  const endpoint = `api/product-order-data/get-100-days-ror/${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  return await reportsAPI.get(endpoint);
};

export const get100DaysRORBasedOnHistory = async (client_ids = []) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (client_ids?.length > 0) {
    client_ids.forEach((id) => params.append("client_ids[]", id));
  }

  const endpoint = `api/product-order-data/get-100-days-ror-based-on-history/${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  return await reportsAPI.get(endpoint);
};

export const processYoungerSales = async (
  client_ids = [],
  brand_ids = [],
  lists = [],
  segments = [],
  mail_codes = [],
  products = [],
  categories = [],
  ror = 1
) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (client_ids?.length > 0) {
    client_ids.forEach((id) => params.append("client_ids[]", id));
  }

  if (brand_ids?.length > 0) {
    brand_ids.forEach((id) => params.append("brand_ids[]", id));
  }

  if (lists?.length > 0) {
    lists.forEach((list) => params.append("lists[]", list));
  }

  if (segments?.length > 0) {
    segments.forEach((segment) => params.append("segments[]", segment));
  }

  if (mail_codes?.length > 0) {
    mail_codes.forEach((code) => params.append("mail_codes[]", code));
  }

  if (products?.length > 0) {
    products.forEach((product) => params.append("products[]", product));
  }

  if (categories?.length > 0) {
    categories.forEach((category) => params.append("categories[]", category));
  }

  if (ror !== undefined && ror !== null) {
    params.append("ror", ror * 0.01);
  }

  const endpoint = `api/product-order-data/process-younger-sales/${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  return await reportsAPI.get(endpoint);
};

export const getProductOrderDataByBrandId = async (brand_id, dateFrom, dateTo) => {
  setAuthHeader();
  const params = { brand_id: brand_id };
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  return await reportsAPI.get("api/product-order-data/", { params });
};

export const getProductOrderDataByFileId = async (fileId) => {
  setAuthHeader();
  return await reportsAPI.get("api/product-order-data/", { params: { file_id: fileId } });
};

export const getProductOrderDataByFileIds = async (fileIds, options = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  fileIds.forEach((id) => params.append("file_ids", id));
  if (options.limit) params.append("limit", options.limit);
  if (options.offset !== undefined && options.offset !== null) params.append("offset", options.offset);
  if (options.lastId !== undefined && options.lastId !== null) params.append("last_id", options.lastId);
  if (options.withMeta) params.append("with_meta", "1");
  if (options.includeTotal) params.append("include_total", "1");
  return await reportsAPI.get(`api/product-order-data/?${params.toString()}`, {
    timeout: 600000,
  });
};

export const getProductOrderDataSummaryByFileIds = async (fileIds) => {
  setAuthHeader();
  const params = new URLSearchParams();
  fileIds.forEach((id) => params.append("file_ids", id));
  return await reportsAPI.get(`api/product-order-data/summary/?${params.toString()}`, {
    timeout: 600000,
  });
};

export const exportSalesDumpCsv = async (fileIds) => {
  setAuthHeader();
  const params = new URLSearchParams();
  fileIds.forEach((id) => params.append("file_ids", id));
  return await reportsAPI.get(`api/export-salesdump-csv/?${params.toString()}`, {
    responseType: "blob",
    timeout: 600000,
  });
};

export const exportSalesDumpExcel = async (fileIds) => {
  setAuthHeader();
  const params = new URLSearchParams();
  fileIds.forEach((id) => params.append("file_ids", id));
  return await reportsAPI.get(`api/export-salesdump-excel/?${params.toString()}`, {
    responseType: "blob",
    timeout: 600000,
  });
};

export const downloadSavedSalesDumpExcel = async (fileId) => {
  setAuthHeader();
  return await reportsAPI.get(`api/salesdump-download/${fileId}/`, {
    responseType: "blob",
    timeout: 600000,
  });
};

export const exportMergedSavedSalesDumpExcel = async (fileIds) => {
  setAuthHeader();
  const params = new URLSearchParams();
  fileIds.forEach((id) => params.append("file_ids", id));
  return await reportsAPI.get(`api/export-saved-salesdump-excel-merged/?${params.toString()}`, {
    responseType: "blob",
    timeout: 600000,
  });
};

export const exportMergedMailingLtvExcel = async (fileIds) => {
  setAuthHeader();
  const params = new URLSearchParams();
  fileIds.forEach((id) => params.append("file_ids", id));
  return await reportsAPI.get(`api/export-saved-mailing-ltv-excel-merged/?${params.toString()}`, {
    responseType: "blob",
    timeout: 600000,
  });
};

export const getMailingResponseDataByFileId = async (report_file_id) => {
  setAuthHeader();
  return await reportsAPI.get("api/mailing-response-data/", {
    params: { report_file_id: report_file_id },
  });
};

export const getMailingResponseDataByFileIds = async (fileIds) => {
  setAuthHeader();
  const params = new URLSearchParams();
  fileIds.forEach((id) => params.append("report_file_ids", id));
  return await reportsAPI.get(`api/mailing-response-data/?${params.toString()}`);
};

export const getMailingResponseDataByBrandIds = async (brandIds) => {
  setAuthHeader();
  return await reportsAPI.get("api/mailing-response-data/", {
    params: { brand_ids: brandIds },
  });
};

export const uploadStockDataFile = async (data) => {
  setAuthHeader();
  return await reportsAPI.post("api/stock-files/", data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const createStockData = async (data) => {
  setAuthHeader();
  return await reportsAPI.post("api/stock-data/", data);
};

export function syncStockData(brand_id) {
  setAuthHeader();
  return reportsAPI.post("api/stock-data/sync-shipstation/", { brand_id }, { timeout: 300000 });
}

export const fetchStockDataByBrandId = async (brandId) => {
  setAuthHeader();
  return await reportsAPI.get("api/stock-data/", {
    params: { brand_id: brandId },
  });
};
export const getStockDataFileById = async (id) => {
  setAuthHeader();
  return await reportsAPI.get(`api/stock-files/${id}/`);
};

export const updateStockDataFile = async (id, data) => {
  setAuthHeader();
  return await reportsAPI.put(`api/stock-files/${id}/`, data);
};

// Get only the filter options without full report data
export const getFilterOptions = async (id = null, filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (id) {
    params.append("id", id);
  }
  
  params.append("request_type", "filter_options");

  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      value.forEach((v) => params.append(key.toLowerCase(), v));
    } else if (
      value !== null &&
      value !== undefined &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      params.append(key.toLowerCase(), value);
    }
  });

  const endpoint = `api/report-files/filter-options/${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  return await reportsAPI.get(endpoint);
};

export const triggerPasRadeFetch = async (brandId) => {
  setAuthHeader();
  return await reportsAPI.post("api/fetch-pasrade/", { brand_id: brandId }, { timeout: 600000 });
};

export const triggerPasRadeSalesDump = async (brandId) => {
  setAuthHeader();
  return await reportsAPI.post("api/fetch-pasrade-salesdump/", { brand_id: brandId }, { timeout: 600000 });
};

export const listPasRadeBrands = async () => {
  setAuthHeader();
  return await reportsAPI.get("api/pasrade-brands/");
};

export const listPasRadeLTVDownloads = async (brandIds = []) => {
  setAuthHeader();
  const params = new URLSearchParams();
  brandIds.forEach((id) => params.append("brand_ids", id));
  return await reportsAPI.get(`api/pasrade-ltv-downloads/?${params.toString()}`);
};

export const listPasRadeSalesDumpDownloads = async (brandIds = []) => {
  setAuthHeader();
  const params = new URLSearchParams();
  brandIds.forEach((id) => params.append("brand_ids", id));
  return await reportsAPI.get(`api/pasrade-salesdump-downloads/?${params.toString()}`);
};

export const fetchMonthlyCashTotals = async (year) => {
  setAuthHeader();
  return await reportsAPI.get(`api/monthly-cash-totals/`, { params: { year } });
};

export const fetchCashControlData = async (dateStr) => {
  setAuthHeader();
  return await reportsAPI.get(`api/cash-control-data/`, { params: { date: dateStr } });
};
